import type { JSX } from "react";

import { useDisclosure } from "@mantine/hooks";
import { useTimer } from "react-timer-hook";

import { useAnalytics } from "@/analytics";
import { useLogger } from "@/sdk";

import { useQuizChallenge } from "../hooks/useQuizChallenge";
import { MicroLearningModal } from "./MicroLearningModal";

export function MicroLearning(): JSX.Element | null {
    const logger = useLogger({ module: "MicroLearning", supportTeam: "sales" });
    const { dispatcher } = useAnalytics();

    const [opened, { open, close }] = useDisclosure(false);
    const { data, isError, isLoading, invalidateQuizQuestion, setCorrectAnswers } = useQuizChallenge();

    useTimer({
        expiryTimestamp: new Date(Date.now() + 5000),
        autoStart: true,
        onExpire: () => {
            open();
            void dispatcher.dispatchBusinessEvent("OpenMicroLearningModal", {
                questionId: data?.question?.questionId,
                question: data?.question?.question,
            });
        },
    });

    if (isLoading || isError || !data) {
        return null;
    }

    const { question, quiz, topic, correctAnswers, totalQuestions, isQuizDone } = data;

    if (!quiz || !topic) {
        return null;
    }

    if (!quiz.announcement.ackStart) {
        void invalidateQuizQuestion();
        return null;
    }

    if (!question) {
        logger.warn("no questions left");
        void dispatcher.dispatchAction("getQuestionnaire", "Expert has no questions left");
        return null;
    }

    if (isQuizDone) {
        logger.debug("quiz has ended");
        return null;
    }

    return (
        <MicroLearningModal
            isOpen={opened}
            close={close}
            invalidateQuizQuestion={invalidateQuizQuestion}
            setCorrectAnswers={setCorrectAnswers}
            question={question}
            topic={topic}
            totalNumberOfQuestions={totalQuestions}
            currentQuestionNumber={correctAnswers}
        />
    );
}
